import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Progress, StatusBadge, Tooltip } from "@sagot/ui";
import { consoleStage } from "../shared/ingestion";
import { failedPages, pagesProgress, syncPhase } from "../shared/sync";
import type { SourceView } from "../shared/types";

type StatusSource = Pick<SourceView, "title" | "status" | "stage" | "progress" | "status_reason" | "sync">;

const K = "deployKnowledge.knowledge.stages";

function FailedStatus({ source }: { source: StatusSource }) {
  const { t } = useTranslation();
  const label = `${t(`${K}.failed`)}${source.status_reason ? ` – ${source.status_reason}` : ""}`;
  const badge = <StatusBadge status="danger" label={label} className="max-w-64 truncate" tabIndex={source.status_reason ? 0 : undefined} />;
  return source.status_reason ? <Tooltip content={source.status_reason}>{badge}</Tooltip> : badge;
}

/** Ready, but some pages of the last sync could not be processed: a warning with a way in. */
function WarningStatus({ source, detailsHref }: { source: StatusSource; detailsHref: string | undefined }) {
  const { t } = useTranslation();
  const badge = <StatusBadge status="warning" label={t(`${K}.readyWithWarnings`, { count: failedPages(source) })} tabIndex={source.status_reason ? 0 : undefined} />;
  return (
    <div className="flex flex-col items-start gap-1">
      {source.status_reason ? <Tooltip content={source.status_reason}>{badge}</Tooltip> : badge}
      {detailsHref ? (
        <AppLink href={detailsHref} aria-label={t(`${K}.detailsFor`, { name: source.title })} className="text-xs font-semibold text-primary-ink hover:underline">
          {t(`${K}.details`)}
        </AppLink>
      ) : null}
    </div>
  );
}

function ProcessingStatus({ source }: { source: StatusSource }) {
  const { t } = useTranslation();
  const pages = pagesProgress(source);
  const stage = consoleStage({ status: source.status, stage: source.stage });
  const stageLabel = source.status === "queued" ? t(`${K}.queued`) : t(`${K}.${stage}`);
  const label = pages ? t(`${K}.processingPages`, { done: pages.settled, total: pages.total }) : stageLabel;
  const progressLabel = pages
    ? t(`${K}.progressPages`, { name: source.title, done: pages.settled, total: pages.total, percent: source.progress })
    : t(`${K}.progress`, { name: source.title, stage: stageLabel, percent: source.progress });
  return (
    <div className={pages ? "w-48" : "w-32"}>
      <div className="flex items-center justify-between gap-2">
        <Badge tone="brand">{label}</Badge>
        <span className="font-mono text-[11px] text-fg-subtle">{source.progress}%</span>
      </div>
      <Progress className="mt-1.5" size="sm" value={source.progress} label={progressLabel} />
    </div>
  );
}

/**
 * Source status: Processing (stage, or "37/43 pages" for a multi-page sync, with progress) ->
 * Ready, Ready with warnings (failed pages + details link) or Failed (+ reason tooltip).
 * Every state is icon/shape + text + colour, never colour alone.
 */
export function SourceStatus({ source, detailsHref }: { source: StatusSource; detailsHref?: string }) {
  const { t } = useTranslation();
  const phase = syncPhase(source);
  if (phase === "disabled") return <StatusBadge status="neutral" icon="pause" label={t(`${K}.disabled`)} />;
  if (phase === "failed") return <FailedStatus source={source} />;
  if (phase === "warnings") return <WarningStatus source={source} detailsHref={detailsHref} />;
  if (phase === "ready") {
    return (
      <Badge tone="success" dot>
        {t(`${K}.ready`)}
      </Badge>
    );
  }
  return <ProcessingStatus source={source} />;
}
